import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { TagBadge } from '@/components/tags/tag-badge';
import {
  RuleBuilder,
  type BuilderState,
  conditionToState,
  stateToCondition,
} from '@/components/segments/rule-builder';
import { useTags, useCreateTag, useDeleteTag, useUpdateTag } from '@/hooks/use-tags';
import { useAuthContext } from '@/contexts/auth-context';
import type { Tag, TagKind, CreateTagInput } from '@/types/tags';
import { formatDate } from '@/lib/utils';

type TabKind = 'label' | 'segment' | 'stage' | 'milestone';

const tabLabels: Record<TabKind, string> = {
  label: 'Labels',
  segment: 'Segments',
  stage: 'Stages',
  milestone: 'Milestones',
};

const colorOptions = [
  'gray',
  'blue',
  'teal',
  'green',
  'emerald',
  'amber',
  'orange',
  'violet',
  'red',
  'pink',
];

export default function TagsPage() {
  const [activeKind, setActiveKind] = useState<TabKind>('label');
  const { data: tags = [], isLoading } = useTags({ kind: activeKind });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Tag | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (tag: Tag) => {
    setEditing(tag);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Tags</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          New tag
        </Button>
      </div>

      <Tabs value={activeKind} onValueChange={(v) => setActiveKind((v ?? 'label') as TabKind)}>
        <TabsList>
          {(Object.keys(tabLabels) as TabKind[]).map((k) => (
            <TabsTrigger key={k} value={k}>
              {tabLabels[k]}
            </TabsTrigger>
          ))}
        </TabsList>
        {(Object.keys(tabLabels) as TabKind[]).map((k) => (
          <TabsContent key={k} value={k} className="mt-4">
            {isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <TagsTable tags={tags} onEdit={openEdit} />
            )}
          </TabsContent>
        ))}
      </Tabs>

      <TagDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        tag={editing}
        defaultKind={activeKind}
      />
    </div>
  );
}

function TagsTable({ tags, onEdit }: { tags: Tag[]; onEdit: (tag: Tag) => void }) {
  const { user } = useAuthContext();
  const deleteTag = useDeleteTag();

  // Admins can edit and delete everything, including system segments /
  // stages / milestones — tags are soft-deleted (migration 019) and
  // assignments are soft-deleted (migration 020), so historical data stays
  // intact even after a system tag is removed. Teachers can only edit and
  // delete their own teacher-scope tags.
  const canEdit = (tag: Tag): boolean => {
    if (user?.role === 'admin') return true;
    return !tag.is_system && tag.scope === 'teacher' && tag.owner_id === user?.id;
  };
  const canDelete = (tag: Tag): boolean => canEdit(tag);

  const handleDelete = async (tag: Tag) => {
    const prompt = tag.is_system
      ? `Delete system ${tag.kind} "${tag.name}"?\n\nThis tag is part of the built-in carma_progression system. ` +
        `It will be soft-deleted — historical assignments are preserved, but students will lose the current assignment and the nightly sweep will stop evaluating it. ` +
        `You cannot restore it from the UI.`
      : `Delete tag "${tag.name}"?`;
    if (!confirm(prompt)) return;
    try {
      await deleteTag.mutateAsync(tag.id);
      toast.success('Tag deleted');
    } catch {
      toast.error('Failed to delete tag');
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Slug</TableHead>
            <TableHead>Scope</TableHead>
            <TableHead>Users</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tags.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground">
                No tags yet.
              </TableCell>
            </TableRow>
          ) : (
            tags.map((tag) => (
              <TableRow key={tag.id}>
                <TableCell>
                  <TagBadge tag={tag} />
                  {tag.is_system && (
                    <span className="ml-2 text-xs text-muted-foreground">system</span>
                  )}
                </TableCell>
                <TableCell className="font-mono text-xs">{tag.slug}</TableCell>
                <TableCell className="capitalize">{tag.scope}</TableCell>
                <TableCell>
                  {(tag.assignment_count ?? 0) > 0 ? (
                    <Link
                      to={`/students?tag_id=${tag.id}`}
                      className="font-medium text-primary underline-offset-2 hover:underline"
                      title={`View students tagged ${tag.name}`}
                    >
                      {tag.assignment_count}
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </TableCell>
                <TableCell>{formatDate(tag.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canEdit(tag) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(tag)}
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {canDelete(tag) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(tag)}
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

interface TagDialogProps {
  open: boolean;
  onClose: () => void;
  tag: Tag | null;
  defaultKind: TabKind;
}

function TagDialog({ open, onClose, tag, defaultKind }: TagDialogProps) {
  const { user } = useAuthContext();
  const createTag = useCreateTag();
  const updateTag = useUpdateTag();

  const editing = tag !== null;
  const kind: TagKind = editing ? tag!.kind : defaultKind;

  const [slug, setSlug] = useState(tag?.slug ?? '');
  const [name, setName] = useState(tag?.name ?? '');
  const [description, setDescription] = useState(tag?.description ?? '');
  const [color, setColor] = useState(tag?.color ?? 'gray');
  const [ruleState, setRuleState] = useState<BuilderState>(conditionToState(tag?.rule));

  // Reset form whenever the dialog opens for a different tag.
  useEffect(() => {
    if (!open) return;
    setSlug(tag?.slug ?? '');
    setName(tag?.name ?? '');
    setDescription(tag?.description ?? '');
    setColor(tag?.color ?? 'gray');
    setRuleState(conditionToState(tag?.rule));
  }, [open, tag]);

  const isAdmin = user?.role === 'admin';
  const canPickKind = !editing && (isAdmin || true);
  // Segments and stages require a rule. Milestones may optionally carry one
  // (rule-awarded milestones are append-only — awarded when the rule first
  // matches, never revoked). Labels never carry a rule.
  const rulesAllowed = kind === 'segment' || kind === 'stage' || kind === 'milestone';
  const rulesRequired = kind === 'segment' || kind === 'stage';

  const submit = async () => {
    if (!name.trim() || !slug.trim()) {
      toast.error('Name and slug are required.');
      return;
    }
    const rule = rulesAllowed ? stateToCondition(ruleState) ?? undefined : undefined;
    if (rulesRequired && !rule) {
      toast.error('Add at least one rule.');
      return;
    }
    try {
      if (editing && tag) {
        await updateTag.mutateAsync({
          id: tag.id,
          input: { name, description: description || null, color, rule },
        });
        toast.success('Tag updated');
      } else {
        const input: CreateTagInput = {
          slug,
          name,
          description: description || undefined,
          kind,
          scope: isAdmin ? 'system' : 'teacher',
          color,
          rule,
        };
        await createTag.mutateAsync(input);
        toast.success('Tag created');
      }
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save tag';
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit tag' : `Create ${kind}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Slug</Label>
              <Input
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                disabled={editing}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input
              value={description ?? ''}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Color</Label>
              <Select value={color} onValueChange={(v) => setColor(v ?? 'gray')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colorOptions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {canPickKind && (
              <div className="space-y-2 text-sm">
                <Label>Kind</Label>
                <p className="capitalize text-muted-foreground">{kind}</p>
              </div>
            )}
          </div>
          {rulesAllowed && (
            <div className="space-y-2 rounded-md border p-3">
              <div className="flex items-center justify-between">
                <Label>
                  Rule
                  {rulesRequired ? (
                    <span className="ml-1 text-xs text-muted-foreground">(required)</span>
                  ) : (
                    <span className="ml-1 text-xs text-muted-foreground">(optional)</span>
                  )}
                </Label>
              </div>
              {kind === 'milestone' && (
                <p className="text-xs text-muted-foreground">
                  Milestones are append-only: this rule determines when the milestone is first
                  awarded. Once a student earns it, breaking the rule later does not remove it.
                  Leave empty to award this milestone only via service-layer event hooks.
                </p>
              )}
              <RuleBuilder state={ruleState} onChange={setRuleState} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={createTag.isPending || updateTag.isPending}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

